import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Descriptions, Button, Input, Select, message } from 'antd';
import PhoneInput from 'react-phone-number-input/input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { SupportedEMRs } from '../../../containers/walkthrough/constants';

import { formatValidFaxNumber } from '../../../global/validation';
import { EditFilled } from '@ant-design/icons';

const { Option } = Select;

const descStyle = {
  paddingLeft: '40px',
  paddingRight: '40px',
  marginTop: '30px',
  textAlign: 'left',
};

const chromeExtLink = `https://chrome.google.com/webstore/detail/medsender/${process.env.CHROME_EXT_ID}`;

class userInformation extends Component {
  static propTypes = {
    getUserInfo: PropTypes.func.isRequired,
    changeEmailPreference: PropTypes.func.isRequired,
    updateUserDetails: PropTypes.func.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    jobTitle: PropTypes.string,
    emr: PropTypes.string,
    departments: PropTypes.array,
    default_fax_numbers: PropTypes.array,
    associatedFaxNumbers: PropTypes.array,
    adminLoginSuccess: PropTypes.bool.isRequired,
    loginErrorMessage: PropTypes.string.isRequired,
    loginAdmin: PropTypes.func.isRequired,
    notificationErrorMessage: PropTypes.string.isRequired,
    allowedUserDomains: PropTypes.array.isRequired,
    defaultFaxNumber: PropTypes.string,
    organizationDetails: PropTypes.shape({
      multiple_organization_login_enabled: PropTypes.bool.isRequired,
      show_multiple_organization_login_modal: PropTypes.bool.isRequired,
      organizations: PropTypes.array.isRequired,
      current_organization: PropTypes.string,
    }),
    openModal: PropTypes.func.isRequired,
    sendOtpToSms: PropTypes.func.isRequired,
    verifySmsOtp: PropTypes.func.isRequired,
  };

  static defaultProps = {
    name: null,
    email: null,
    phoneNumber: null,
    jobTitle: null,
    emr: null,
    departments: null,
    default_fax_numbers: [],
    associatedFaxNumbers: [],
    defaultFaxNumber: null,
    organizationDetails: null,
  };

  state = {
    name: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
    emr: '',
    departments: [],
    defaultFaxNumber: '',
    defaultFaxNumbers: [],
    associatedFaxNumbers: [],
    isAdmin: false,
    loginError: false,
    loginErrorMessage: '',
    checked: true,
    loading: false,
    chromeExtensionInstalled: false,
    userEMR: localStorage.getItem('m8_emr') || '',
    organizationDetails: {
      multiple_organization_login_enabled: false,
      currentOrganization: '',
    },
    // Inline cell-phone editor. The number is the SMS destination for
    // two-step login, so changing it is a verified step machine, not a free
    // text field: confirm possession of the CURRENT number first (when one
    // exists), then verify the new number by texted code.
    // Steps: idle | old_intro | old_code | new_number | new_code
    phoneStep: 'idle',
    phoneDraft: '',
    phonePendingNumber: '',
    phoneValid: false,
    phoneCode: '',
    phoneBusy: false,
    phoneError: '',
  };

  componentDidMount() {
    this.setInformation();
    this.showChromeExtensionInformation();
  }

  openAddUserModal = () => {
    const { openModal } = this.props;
    const modalType = {
      type: 'ADD_NEW_USER',
    };
    openModal(modalType);
  }

  startPhoneEdit = () => {
    const { phoneNumber } = this.state;
    this.setState({
      phoneStep: phoneNumber ? 'old_intro' : 'new_number',
      phoneDraft: '',
      phonePendingNumber: '',
      phoneValid: false,
      phoneCode: '',
      phoneBusy: false,
      phoneError: '',
    });
  };

  cancelPhoneEdit = () => {
    this.setState({
      phoneStep: 'idle', phoneDraft: '', phonePendingNumber: '', phoneValid: false, phoneCode: '', phoneBusy: false, phoneError: '',
    });
  };

  // Texts a code to the number the current step is about: the EXISTING number
  // while confirming possession, the new number afterwards.
  sendPhoneCode = async () => {
    const { sendOtpToSms } = this.props;
    const { phoneStep, phoneDraft, phoneNumber } = this.state;
    const confirmingOld = phoneStep === 'old_intro' || phoneStep === 'old_code';
    const target = confirmingOld ? phoneNumber : phoneDraft;
    this.setState({ phoneBusy: true, phoneError: '', phonePendingNumber: confirmingOld ? '' : target });
    let result;
    try {
      result = await sendOtpToSms(target);
    } catch (error) {
      result = { error };
    }
    const { phoneStep: stepNow } = this.state;
    if (stepNow === 'idle') return; // canceled while the request was in flight
    if (result && result.error) {
      this.setState({
        phoneBusy: false,
        phonePendingNumber: '',
        phoneError: result.response?.data?.error || 'We could not text that number. Please check it and try again.',
      });
      return;
    }
    this.setState({
      phoneBusy: false,
      phoneDraft: confirmingOld ? phoneDraft : target,
      phoneCode: '',
      phoneError: '',
      phonePendingNumber: confirmingOld ? '' : target,
      phoneStep: confirmingOld ? 'old_code' : 'new_code',
    });
  };

  verifyPhoneCode = async () => {
    const { verifySmsOtp, getUserInfo } = this.props;
    const {
      phoneStep, phoneDraft, phonePendingNumber, phoneNumber, phoneCode, phoneBusy,
    } = this.state;
    if (phoneCode.length !== 6 || phoneBusy) return;
    const confirmingOld = phoneStep === 'old_code';
    // Verifying against the existing number proves possession without changing
    // it (the endpoint writes back the same value); the final step verifies
    // and saves the new number.
    const target = confirmingOld ? phoneNumber : (phonePendingNumber || phoneDraft);
    this.setState({ phoneBusy: true, phoneError: '' });
    let result;
    try {
      result = await verifySmsOtp(phoneCode, target);
    } catch (error) {
      result = { error };
    }
    const { phoneStep: stepNow } = this.state;
    if (stepNow === 'idle') return; // canceled while the request was in flight
    const verified = result && !result.error && result.data?.message === 'success';
    if (!verified) {
      this.setState({ phoneBusy: false, phoneError: 'That code didn\'t match. Please try again.' });
      return;
    }
    if (confirmingOld) {
      this.setState({
        phoneBusy: false, phonePendingNumber: '', phoneCode: '', phoneError: '', phoneStep: 'new_number',
      });
      return;
    }
    this.setState({
      phoneNumber: target,
      phoneStep: 'idle',
      phoneDraft: '',
      phonePendingNumber: '',
      phoneValid: false,
      phoneBusy: false,
      phoneCode: '',
    });
    message.success({ content: 'Cell phone number updated' });
    getUserInfo();
  };

  renderPhoneCodeInput = () => {
    const { phoneCode } = this.state;
    return (
      <Input
        className="phone-inline__code"
        maxLength={6}
        placeholder="6-digit code"
        value={phoneCode}
        onChange={(e) => this.setState({ phoneCode: e.target.value.replace(/\D/g, ''), phoneError: '' })}
        onPressEnter={this.verifyPhoneCode}
      />
    );
  };

  renderPhoneCell = () => {
    const {
      phoneNumber, phoneStep, phoneDraft, phonePendingNumber, phoneValid, phoneCode, phoneBusy, phoneError,
    } = this.state;

    if (phoneStep === 'idle') {
      return (
        <div className="phone-inline">
          <span>{phoneNumber || 'N/A'}</span>
          <Button type="link" className="phone-inline__action" onClick={this.startPhoneEdit}>
            {phoneNumber ? 'Change' : 'Add'}
          </Button>
          <div className="phone-inline__hint">
            When set, sign-in verification codes are texted to this number.
          </div>
        </div>
      );
    }

    return (
      <div className="phone-inline">
        {phoneStep === 'old_intro' && (
          <>
            <Button type="primary" loading={phoneBusy} onClick={this.sendPhoneCode}>
              {`Text a code to ${phoneNumber}`}
            </Button>
            <Button type="link" className="phone-inline__action" onClick={this.cancelPhoneEdit}>Cancel</Button>
            <div className="phone-inline__hint">
              First we confirm you still have your current number — then you can enter a new one.
            </div>
          </>
        )}
        {phoneStep === 'old_code' && (
          <>
            {this.renderPhoneCodeInput()}
            <Button
              type="primary"
              loading={phoneBusy}
              disabled={phoneCode.length !== 6}
              onClick={this.verifyPhoneCode}
            >
              Confirm
            </Button>
            <Button type="link" className="phone-inline__action" disabled={phoneBusy} onClick={this.sendPhoneCode}>Resend</Button>
            <Button type="link" className="phone-inline__action" onClick={this.cancelPhoneEdit}>Cancel</Button>
            <div className="phone-inline__hint">
              {`Enter the code we texted to your current number, ${phoneNumber}.`}
            </div>
          </>
        )}
        {phoneStep === 'new_number' && (
          <>
            <span className="phone-inline__cc">(+1)</span>
            <PhoneInput
              country="US"
              placeholder="(555) 555-1234"
              className="ant-input phone-inline__input"
              disabled={phoneBusy}
              value={phoneDraft}
              onChange={(number) => {
                if (phoneBusy) return;
                this.setState({
                  phoneDraft: number || '',
                  phoneValid: number ? isValidPhoneNumber(number) : false,
                });
              }}
            />
            <Button
              type="primary"
              loading={phoneBusy}
              disabled={!phoneValid}
              onClick={this.sendPhoneCode}
            >
              Text me a code
            </Button>
            <Button type="link" className="phone-inline__action" onClick={this.cancelPhoneEdit}>Cancel</Button>
            <div className="phone-inline__hint">
              We&apos;ll text a verification code to confirm this number — it becomes where your sign-in codes go.
            </div>
          </>
        )}
        {phoneStep === 'new_code' && (
          <>
            {this.renderPhoneCodeInput()}
            <Button
              type="primary"
              loading={phoneBusy}
              disabled={phoneCode.length !== 6}
              onClick={this.verifyPhoneCode}
            >
              Verify & save
            </Button>
            <Button type="link" className="phone-inline__action" disabled={phoneBusy} onClick={this.sendPhoneCode}>Resend</Button>
            <Button type="link" className="phone-inline__action" onClick={this.cancelPhoneEdit}>Cancel</Button>
            <div className="phone-inline__hint">
              {`Enter the code we texted to ${phonePendingNumber || phoneDraft}.`}
            </div>
          </>
        )}
        {phoneError && <div className="phone-inline__error">{phoneError}</div>}
      </div>
    );
  };

  openChangeEMR = () => {
    const { openModal, emr } = this.props;
    const modalType = {
      data: { emr },
      type: 'CHANGE_EMR',
    };
    openModal(modalType);
  }

  setInformation = async () => {
    this.setState({ loading: true });
    const { getUserInfo } = this.props;
    await getUserInfo();
    this.setState({ loading: false });
    const {
      name,
      email,
      jobTitle,
      phoneNumber,
      emr,
      departments,
      default_fax_numbers: defaultFaxNumbers,
      associatedFaxNumbers,
      defaultFaxNumber,
      notification_fax_email,
      isAdmin,
      allowedUserDomains,
      organizationDetails,
    } = this.props;

    localStorage.setItem('allowed_user_domains', JSON.stringify(allowedUserDomains));
    this.setState({
      name,
      email,
      phoneNumber,
      jobTitle,
      emr,
      departments,
      defaultFaxNumbers,
      associatedFaxNumbers,
      isAdmin,
      checked: notification_fax_email,
      userEMR: localStorage.getItem('m8_emr') || '',
      defaultFaxNumber,
      organizationDetails,
    });
  }

  onCheck = async (event, isInputChecked) => {
    const { changeEmailPreference } = this.props;
    this.setState({
      checked: isInputChecked,
    });
    await changeEmailPreference(isInputChecked);
    const { notificationErrorMessage } = this.props;
    if (notificationErrorMessage !== '') {
      /* global toastr */
      toastr.error(notificationErrorMessage);
    } else {
      const toastrMessage = isInputChecked
        ? 'You will now receive email notifications when receiving records'
        : 'You will no longer receive email notifications when receiving records';
      toastr.success(toastrMessage);
    }
  }

  checkChromeExtensionInstallation = () => {
    const ping = { message: 'ping' };
    const chromeExtId = process.env.CHROME_EXT_ID;
    return new Promise((resolve, reject) => {
      /* global chrome */
      chrome.runtime.sendMessage(chromeExtId, ping, (response) => {
        if (response && response.message) return resolve();
        return reject(chrome.runtime.lastError.message || 'Error');
      });
    });
  }

  showChromeExtensionInformation = () => {
    this.checkChromeExtensionInstallation()
      .then(() => {
        this.setState({ chromeExtensionInstalled: true });
      })
      .catch(() => {
        this.setState({ chromeExtensionInstalled: false });
      });
  }

  installWindowsClient = () => {
    const win = window.open(process.env.DOWNLOAD_APP_LINK, '_blank');
    win.opener = null;
  }

  changeDefaultOrganization = () => {
    const { openModal: handleOpenModal } = this.props;
    const { organizationDetails } = this.state;
    const modalType = {
      type: 'SELECT_ORGANIZATION',
      data: organizationDetails,
    };
    handleOpenModal(modalType);
  }

  handleDefaultFaxChange = async (selectedFaxNumber) => {
    const { updateUserDetails } = this.props;

    try {
      // When clearing, selectedFaxNumber will be undefined
      // Send empty string to backend to clear the default fax number
      await updateUserDetails({ caller_id: selectedFaxNumber || '' }, { rejectOnError: true });

      // Update local state
      this.setState({ defaultFaxNumber: selectedFaxNumber || null });

      // Show success message
      const successMessage = selectedFaxNumber
        ? 'Default fax number updated successfully'
        : 'Default fax number cleared successfully';
      message.success(successMessage);
    } catch {
      message.error('Failed to update default fax number');
    }
  }

  renderDefaultFaxDropdown = () => {
    const { defaultFaxNumber, defaultFaxNumbers } = this.state;

    if (!defaultFaxNumbers || defaultFaxNumbers.length === 0) {
      return 'No fax numbers available';
    }

    const formattedNumber = formatValidFaxNumber(defaultFaxNumber);

    return (
      <Select
        value={formattedNumber || null}
        onChange={this.handleDefaultFaxChange}
        placeholder="Select a default fax number"
        style={{ width: '100%', maxWidth: '300px' }}
        allowClear
      >
        {defaultFaxNumbers.map((faxNumber) => {
          const [number, ownerName] = faxNumber;
          return (
            <Option key={number} value={number}>
              {formatValidFaxNumber(number)} - {ownerName}
            </Option>
          );
        })}
      </Select>
    );
  }

  render() {
    const {
      name,
      email,
      phoneNumber,
      jobTitle,
      emr,
      departments,
      defaultFaxNumber,
      associatedFaxNumbers,
      userEMR,
      chromeExtensionInstalled,
      organizationDetails,
    } = this.state;
    const { multiple_organization_login_enabled: isMultiOrgEnabled, current_organization: currentOrganization } = organizationDetails;
    const departmentNames = departments.map(department => department.name);

    const contents = [
      { label: 'Name', content: name },
      { label: 'Email', content: email },
      { label: 'Cell Phone Number', content: this.renderPhoneCell() },
      { label: 'Job Title', content: jobTitle },
      { label: 'EMR', content: emr, button: { onClick: this.openChangeEMR } },
      { label: 'Default Fax Number', content: this.renderDefaultFaxDropdown() },
      { label: 'Departments', content: departments && departmentNames.join([', ']) },
      {
        label: 'Current Location/Organization/Branch',
        content: currentOrganization,
        hide: !isMultiOrgEnabled,
        button: { onClick: this.changeDefaultOrganization },
      },
    ];

    const platform = window.navigator.platform;
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const onWindows = windowsPlatforms.indexOf(platform) !== -1 && navigator.userAgent.indexOf('Windows NT 5.1') === -1;
    return (
      <>
        <Descriptions title="User Information" bordered style={descStyle}>
          {contents.map(
            ({ label, content, button, hide }) => {
              return (
                !hide && (
                  <Descriptions.Item
                    key={`userInfo-${label}`}
                    span={3}
                    label={label}
                    style={{ textAlign: "left" }}
                  >
                    {content || "N/A"}
                    {button && (
                      <Button
                        type="link"
                        style={{ marginLeft: "5px" }}
                        icon={<EditFilled />}
                        onClick={button.onClick}
                      />
                    )}
                  </Descriptions.Item>
                )
              );
            },
          )}
        </Descriptions>
        {
          associatedFaxNumbers && associatedFaxNumbers.length > 0 ? (
            <Descriptions title="Associated Fax Numbers" bordered style={{ marginTop: '20px', ...descStyle }}>
              {
                associatedFaxNumbers.map((faxNumber, index) => (
                  <Descriptions.Item key={`faxNumbs-${index}`} label={faxNumber[1]} span={3}>
                    {formatValidFaxNumber(faxNumber[0])}
                  </Descriptions.Item>
                ))
              }
            </Descriptions>
          )
            : (<div style={{ paddingTop: '50px', fontStyle: 'italic' }}>There are no fax numbers associated with this account</div>)
        }
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingRight: '40px' }}>
          {
            onWindows && (
              <Button
                style={{ marginRight: '20px '}}
                backgroundColor="rgb(163,237,255)"
                onClick={this.installWindowsClient}
              >
                Install Printer Driver
              </Button>
            )
          }
          {
            !chromeExtensionInstalled && (
              <Button
              type="primary"
              backgroundcolor="rgb(163,237,255)"
              onClick={() => window.open(chromeExtLink, 'chrome link', 'width=1000, height=600')}
              >
                Install Chrome Extension
              </Button>
            )
          }
        </div>
      </>
    );
  }
}

export default userInformation;
